import { useT } from '@/core/I18nProvider';

import type { ComplianceFramework, ComplianceStatus } from '../../content/compliance-status';
import type { ReactElement } from 'react';

/**
 * Phase 6 Pass 3 iter 245 — compliance framework card.
 *
 * Per `pass-3-trust-center.md` § "Compliance status driven by JSON
 * config": "The Trust Center compliance page renders cards from
 * this JSON. Status strings drive badge colors."
 *
 * **Status taxonomy → badge colour (Designer hat, locked)**: each
 * status maps to a Tailwind colour family. Earned / verified
 * statuses lean green (positive); in-progress leans amber; planned
 * leans neutral; out-of-scope leans grey (intentional dim).
 *
 *   - `certified` / `compliance_verified` / `available` → emerald
 *   - `in_progress` → amber
 *   - `planned` → sky (neutral-blue)
 *   - `out_of_scope` → neutral
 *
 * **Accessibility (Accessibility hat, locked)**: status colour is
 * decorative; the status label is also rendered as plain text in
 * the badge so screen-reader users get the same information without
 * relying on colour. Per WCAG 2.1 § 1.4.1 "Use of color".
 *
 * **Three-layer contract preserved**: pure presentational, no
 * effects, no state.
 */

const STATUS_BADGE_CLASSES: Readonly<Record<ComplianceStatus, string>> = {
  certified: 'bg-success/15 text-success-foreground',
  compliance_verified: 'bg-success/15 text-success-foreground',
  available: 'bg-success/15 text-success-foreground',
  in_progress: 'bg-warning/15 text-warning-foreground',
  planned: 'bg-info/15 text-info-foreground',
  out_of_scope: 'bg-[color:var(--bd-sunk)] text-[color:var(--bd-muted)]',
};

/** i18n key per status; the label text is resolved in the active locale. */
const STATUS_LABEL_KEYS: Readonly<Record<ComplianceStatus, string>> = {
  certified: 'compliance.status.certified',
  compliance_verified: 'compliance.status.complianceVerified',
  available: 'compliance.status.available',
  in_progress: 'compliance.status.inProgress',
  planned: 'compliance.status.planned',
  out_of_scope: 'compliance.status.outOfScope',
};

export interface ComplianceCardProps {
  readonly framework: ComplianceFramework;
}

export function ComplianceCard({ framework }: ComplianceCardProps): ReactElement {
  const { t } = useT();
  return (
    <article className="rounded-lg border border-[color:var(--bd-line)] p-5">
      {/* `min-w-0` on the title and `shrink-0` on the badge. Without them BOTH
          flex items refuse to shrink — the title because a flex item's default
          `min-width: auto` is its longest word, the badge because it is
          `whitespace-nowrap` — so the pair overflows the card rather than the
          title wrapping. German is where it showed (a 82px "Verfügbar" badge
          pushed 7px past the viewport at 390px); English fitted, which is why
          it survived review. */}
      <header className="mb-3 flex items-start justify-between gap-3">
        <h3 className="min-w-0 text-lg font-semibold leading-tight text-[color:var(--bd-ink)]">
          {t(framework.labelKey)}
        </h3>
        <span
          className={`shrink-0 whitespace-nowrap rounded-full px-3 py-0.5 text-xs font-semibold ${STATUS_BADGE_CLASSES[framework.status]}`}
        >
          {t(STATUS_LABEL_KEYS[framework.status])}
        </span>
      </header>
      <p className="mb-3 text-sm text-[color:var(--bd-muted)]">{t(framework.notesKey)}</p>
      <dl className="grid grid-cols-1 gap-1 text-sm text-[color:var(--bd-muted)] sm:grid-cols-2">
        {framework.targetKey !== null && (
          <div>
            <dt className="inline font-semibold">{t('compliance.field.target')} </dt>
            <dd className="inline">{t(framework.targetKey)}</dd>
          </div>
        )}
        {framework.evidence_provider !== null && (
          <div>
            <dt className="inline font-semibold">{t('compliance.field.evidence')} </dt>
            <dd className="inline">{framework.evidence_provider}</dd>
          </div>
        )}
        {framework.auditor !== null && (
          <div>
            <dt className="inline font-semibold">{t('compliance.field.auditor')} </dt>
            <dd className="inline">{framework.auditor}</dd>
          </div>
        )}
      </dl>
    </article>
  );
}
